import {
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";
import { useAuth, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLazyListVendorShipmentPricesForReportsQuery } from "store/services/reportApi";
import { useLazyGetDdlVendorProfileQuery } from "store/services/vendorProfileApi";
import ExportVendorTransaction from "./ExportVendorTransaction";

const breadcrumbs = [
  { title: "Reports", path: "/app/crm/reports/list" },
  { title: "Vendor Transaction Report" },
];

const VendorTransactionReport = () => {
  const [searchParams] = useSearchParams();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const { viewEntity } = useAuth();

  const [responsibleVendor, setResponsibleVendor] = useState(
    searchParams.get("responsibleVendor") || "all"
  );

  const [
    fetchVendors,
    { data: vendors, isLoading: loadingVendors, isFetching: fetchingVendors },
  ] = useLazyGetDdlVendorProfileQuery();

  const [fetchShipmentPrices, { data: priceRes, isLoading, isFetching }] =
    useLazyListVendorShipmentPricesForReportsQuery();

  const columns = [
    {
      name: "Shop Name",
      selector: (row) => row.shopName,
      cell: (row) => (
        <Link
          title={row?.shopName}
          to={`/app/crm/reports/vendor-transaction-report/vendor/${row?._id}/detail`}
          className="text-blue-600 underline line-clamp-2 dark:hover:text-blue-300"
        >
          {row?.shopName ? <HighlightedText text={row?.shopName} /> : 0}
        </Link>
      ),
    },
    {
      name: "Vendor Name",
      selector: (row) => row.vendorName,
      cell: (row) => (
        <Link
          title={row?.vendorName}
          to={`/app/crm/reports/vendor-transaction-report/vendor/${row?._id}/detail`}
          className="text-blue-600 underline line-clamp-2 dark:hover:text-blue-300"
        >
          {row?.vendorName ? <HighlightedText text={row?.vendorName} /> : 0}
        </Link>
      ),
    },

    // {
    //   name: "Pending",
    //   selector: (row) => row?.totalNotPaid || 0,
    //   cell: (row) =>
    //     row?.totalNotPaid ? `Rs.${Math.abs(row.totalNotPaid).toFixed(2)}` : 0,
    //   sortable: true,
    //   align: "right",
    // },

    {
      name: "Pending COD",
      selector: (row) => row?.codAmount || 0,
      cell: (row) =>
        row?.codAmount ? `Rs. ${Math.abs(row.codAmount).toFixed(2)}` : 0,
      sortable: true,
      colStyle: { color: "red" },
    },

    {
      name: "Pending Charge",
      selector: (row) => row?.chargeAmount || 0,
      cell: (row) =>
        row?.chargeAmount ? `Rs. ${Math.abs(row.chargeAmount).toFixed(2)}` : 0,
      sortable: true,
    },

    {
      name: "Requested",
      selector: (row) => row?.totalPaid || 0,
      cell: (row) =>
        row?.totalPaid ? `Rs. ${Math.abs(row.totalPaid).toFixed(2)}` : 0,
      sortable: true,
    },

    {
      name: "Finance Collected",
      selector: (row) => row?.totalCollected || 0,
      cell: (row) =>
        row?.totalCollected
          ? `Rs. ${Math.abs(row.totalCollected).toFixed(2)}`
          : 0,
      sortable: true,
    },

    {
      name: "Total",
      selector: (row) => row?.total || 0,
      cell: (row) =>
        row?.total ? `Rs. ${Math.abs(row.total).toFixed(2)}` : "N/A",
      sortable: true,
    },
  ];

  const vendorOptions = [
    { label: "All", value: "all" },
    ...(vendors?.map((vendor) => ({
      label: `${vendor?.shopName} (${vendor?.ownerName})`,
      value: vendor._id || vendor._id,
    })) || []),
  ];

  useEffect(() => {
    fetchShipmentPrices({
      vendorProfile:
        responsibleVendor === "all" ? undefined : responsibleVendor,
      limit: perPage,
      page: currentPage,
    });

    fetchVendors({
      account: viewEntity.entity.account._id,
      entity: viewEntity.entity._id,
    });
  }, [
    currentPage,
    fetchShipmentPrices,
    fetchVendors,
    perPage,
    responsibleVendor,
    viewEntity.entity._id,
    viewEntity.entity.account._id,
  ]);

  return (
    <div>
      <BreadcrumbLayout breadcrumbs={breadcrumbs}>
        <BreadcrumbLayout.Error />
        <BreadcrumbLayout.Paper>
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 1.5,
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ minWidth: 180 }}>
                Vendor Transaction Report
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ flex: 1, justifyContent: "flex-end" }}
              >
                <Autocomplete
                  size="small"
                  options={[...vendorOptions]}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value
                  }
                  value={
                    vendorOptions.find(
                      (opt) => opt.value === responsibleVendor
                    ) || {
                      value: "all",
                      label: "All Vendors",
                    }
                  }
                  onChange={(e, newValue) =>
                    setResponsibleVendor(newValue?.value || "all")
                  }
                  disabled={loadingVendors || fetchingVendors}
                  sx={{ minWidth: 300 }}
                  renderInput={(params) => (
                    <TextField {...params} label="Shop Name (Vendor Name)" />
                  )}
                />

                <ExportVendorTransaction />
              </Stack>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {isLoading || isFetching ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : priceRes?.data?.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No data available for the selected filters.
                </Typography>
              </Box>
            ) : (
              <AppTable
                columns={columns}
                data={priceRes?.data ?? []}
                progressPending={isLoading || isFetching}
                paginationServer
                paginationPerPage={perPage}
                paginationTotalRows={priceRes?.totalItems ?? 0}
                paginationDefaultPage={currentPage}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
              />
            )}
          </Box>
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </div>
  );
};

export default VendorTransactionReport;
